import { db } from "@/lib/firebase";
import { IdeaUiOverride } from "@/lib/ideaUi";
import {
  Idea,
  IdeaState,
  safeIdeaParse,
} from "@/schemas/firestore/idea.schema";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { StateCreator } from "zustand";
import { StoreState } from "..";

const IDEA_DRAFT_LOCK_DURATION_MS = 3 * 60 * 1000;
const IDEA_DRAFT_LOCKS_STORAGE_KEY = "crolytics-idea-draft-locks-v1";

interface PersistedIdeaDraftLock {
  userId: string;
  domainId: string;
  ideaId: string;
  createdDraftPageId: string | null;
  draftLockStartedAt: number;
  draftLockExpiresAt: number;
  draftLockWpType: "posts" | "pages" | null;
}

interface MarkIdeaCreatePendingOptions {
  pageId?: string | null;
  wpType?: "posts" | "pages";
  startedAt?: number;
  expiresAt?: number;
}

const getIdeaDraftLockId = (
  userId: string,
  domainId: string,
  ideaId: string,
) => `${userId}:${domainId}:${ideaId}`;

const isIdeaTerminal = (idea: Idea) =>
  idea.status === "implemented" ||
  idea.status === "archived" ||
  idea.status === "error";

function canUseStorage() {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

function readIdeaDraftLocksFromStorage(): Record<string, PersistedIdeaDraftLock> {
  if (!canUseStorage()) {
    return {};
  }

  const raw = localStorage.getItem(IDEA_DRAFT_LOCKS_STORAGE_KEY);
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    localStorage.removeItem(IDEA_DRAFT_LOCKS_STORAGE_KEY);
    return {};
  }
}

function writeIdeaDraftLocksToStorage(
  locks: Record<string, PersistedIdeaDraftLock>,
) {
  if (!canUseStorage()) {
    return;
  }

  if (Object.keys(locks).length === 0) {
    localStorage.removeItem(IDEA_DRAFT_LOCKS_STORAGE_KEY);
    return;
  }

  localStorage.setItem(IDEA_DRAFT_LOCKS_STORAGE_KEY, JSON.stringify(locks));
}

function getActiveIdeaDraftLocksFromStorage(
  now = Date.now(),
): Record<string, PersistedIdeaDraftLock> {
  const storedLocks = readIdeaDraftLocksFromStorage();
  const activeLocks = Object.fromEntries(
    Object.entries(storedLocks).filter(([, lock]) => {
      return (
        lock &&
        typeof lock === "object" &&
        typeof lock.userId === "string" &&
        typeof lock.domainId === "string" &&
        typeof lock.ideaId === "string" &&
        typeof lock.draftLockStartedAt === "number" &&
        typeof lock.draftLockExpiresAt === "number" &&
        lock.draftLockExpiresAt > now
      );
    }),
  );

  if (Object.keys(activeLocks).length !== Object.keys(storedLocks).length) {
    writeIdeaDraftLocksToStorage(activeLocks);
  }

  return activeLocks;
}

function persistIdeaDraftLock(lock: PersistedIdeaDraftLock) {
  const nextLocks = getActiveIdeaDraftLocksFromStorage();
  nextLocks[getIdeaDraftLockId(lock.userId, lock.domainId, lock.ideaId)] = lock;
  writeIdeaDraftLocksToStorage(nextLocks);
}

function clearPersistedIdeaDraftLock(
  userId: string,
  domainId: string,
  ideaId: string,
) {
  const nextLocks = getActiveIdeaDraftLocksFromStorage();
  const lockId = getIdeaDraftLockId(userId, domainId, ideaId);
  if (!(lockId in nextLocks)) {
    return;
  }

  delete nextLocks[lockId];
  writeIdeaDraftLocksToStorage(nextLocks);
}

function getIdeaDraftOverride(
  lock?: PersistedIdeaDraftLock,
): IdeaUiOverride | undefined {
  if (!lock) {
    return undefined;
  }

  return {
    isCreatingDraft: true,
    createdDraftPageId: lock.createdDraftPageId,
    draftLockStartedAt: lock.draftLockStartedAt,
    draftLockExpiresAt: lock.draftLockExpiresAt,
    draftLockWpType: lock.draftLockWpType ?? undefined,
  };
}

function mergeIdeaDraftOverride(
  existing: IdeaUiOverride | undefined,
  lock?: PersistedIdeaDraftLock,
): IdeaUiOverride | undefined {
  if (lock) {
    return {
      ...existing,
      ...getIdeaDraftOverride(lock),
    };
  }

  if (!existing) {
    return undefined;
  }

  const { ...rest } = existing;
  delete rest.isCreatingDraft;
  delete rest.createdDraftPageId;
  delete rest.draftLockStartedAt;
  delete rest.draftLockExpiresAt;
  delete rest.draftLockWpType;
  const hasRest = Object.keys(rest).length > 0;

  return hasRest ? rest : undefined;
}

export interface IdeaSlice extends IdeaState {
  /* actions */
  subscribeToIdeas: (orgId: string, domainId: string) => () => void;
  clearIdeas: () => void;
  unsubscribeIdeas: () => void;
  markIdeaCreatePending: (
    userId: string,
    domainId: string,
    ideaId: string,
    options?: MarkIdeaCreatePendingOptions,
  ) => void;
  resolveIdeaAsImplemented: (
    userId: string,
    domainId: string,
    ideaId: string,
    pageId?: string | null,
    pageType?: "post" | "page" | null,
  ) => void;
  clearIdeaUiOverride: (
    userId: string,
    domainId: string,
    ideaId: string,
  ) => void;

  /* internals */
  _ideaSubscriptions: Map<string, () => void>;
  ideaUiOverrides: Record<string, IdeaUiOverride>;
}

export const createIdeaSlice: StateCreator<
  StoreState,
  [],
  [],
  IdeaSlice
> = (set, get) => ({
  /* ---------- initial state ---------- */
  ideas: [],
  isLoadingIdeas: false,
  ideasError: null,
  _ideaSubscriptions: new Map(),
  ideaUiOverrides: {},

  /* ---------- actions ---------- */
  subscribeToIdeas: (orgId: string, domainId: string) => {
    if (!orgId || !domainId) {
      console.log("Ideas subscription requires orgId and domainId.");
      return () => {};
    }
    const userId = get().currentUser?.uid || "";

    const existingUnsubscribe = get()._ideaSubscriptions.get(domainId);
    if (existingUnsubscribe) {
      console.log(
        `Ideas subscription already exists for domain ${domainId}`,
      );
      return existingUnsubscribe;
    }

    set((state) => {
      const persistedOverrides: Record<string, IdeaUiOverride> = {};
      Object.entries(getActiveIdeaDraftLocksFromStorage())
        .filter(([, lock]) => {
          return lock.userId === userId && lock.domainId === domainId;
        })
        .forEach(([, lock]) => {
          const override = getIdeaDraftOverride(lock);
          if (override) {
            persistedOverrides[lock.ideaId] = override;
          }
        });

      return {
        isLoadingIdeas: state.ideas.length === 0,
        ideasError: null,
        ideaUiOverrides: {
          ...state.ideaUiOverrides,
          ...persistedOverrides,
        },
      };
    });

    console.log(
      `Setting up real-time ideas listener for domain ${domainId}`,
    );

    const ideasRef = collection(
      db,
      `organizations/${orgId}/domains/${domainId}/ideas`,
    );
    const q = query(ideasRef, orderBy("created_at", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ideasList: Idea[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const ideaData = {
            // Spread first, override after. A hand-listed literal silently drops
            // every field nobody remembered to add — which is exactly how
            // `source_thread_id`/`source_message_id` reached the schema but never
            // the UI. Zod strips whatever isn't in `IdeaSchema`, so this is safe.
            ...data,
            id: docSnapshot.id,
            user_id: data.user_id || userId,
            domain_id: domainId,
            status: data.status || "completed",
            title: data.title || "",
            description: data.description || "",
            target_keyword: data.target_keyword || "",
            suggested_slug: data.suggested_slug || "",
            rationale: data.rationale || "",
            target_audience: data.target_audience || "",
            content_outline: data.content_outline || [],
            source_input: data.source_input || "",
            error_message: data.error_message || "",
            implemented_page_id: data.implemented_page_id ?? null,
            implemented_page_type: data.implemented_page_type ?? null,
            implemented_at: data.implemented_at ?? null,
            created_at: data.created_at ?? new Date(),
            updated_at: data.updated_at ?? null,
          };

          const result = safeIdeaParse(ideaData);
          if (result.success) {
            ideasList.push(result.data);
          } else {
            console.error(
              `Validation failed for idea ${docSnapshot.id}:`,
              result.error.issues,
            );
          }
        });

        console.log(
          `Ideas snapshot: ${snapshot.docs.length} docs, ${ideasList.length} valid for domain ${domainId}`,
        );

        set((state) => {
          const other = state.ideas.filter((i) => i.domain_id !== domainId);
          const nextOverrides = { ...state.ideaUiOverrides };
          const activeLocks = getActiveIdeaDraftLocksFromStorage();

          ideasList.forEach((idea) => {
            const lock = activeLocks[getIdeaDraftLockId(userId, domainId, idea.id)];

            if (isIdeaTerminal(idea)) {
              delete nextOverrides[idea.id];
              clearPersistedIdeaDraftLock(userId, domainId, idea.id);
              return;
            }

            const nextOverride = mergeIdeaDraftOverride(
              nextOverrides[idea.id],
              lock,
            );

            if (nextOverride) {
              nextOverrides[idea.id] = nextOverride;
            } else {
              delete nextOverrides[idea.id];
            }
          });

          return {
            ideas: [...other, ...ideasList],
            isLoadingIdeas: false,
            ideaUiOverrides: nextOverrides,
          };
        });
      },
      (error) => {
        console.error(
          `Ideas subscription error for domain ${domainId}:`,
          error,
        );
        set({
          ideasError: "Failed to subscribe to ideas",
          isLoadingIdeas: false,
        });
      },
    );

    const wrappedUnsubscribe = () => {
      unsubscribe();
      get()._ideaSubscriptions.delete(domainId);
      console.log(`Unsubscribed from ideas for domain ${domainId}`);
    };

    set((state) => {
      const newSubscriptions = new Map(state._ideaSubscriptions);
      newSubscriptions.set(domainId, wrappedUnsubscribe);
      return { _ideaSubscriptions: newSubscriptions };
    });

    return wrappedUnsubscribe;
  },

  clearIdeas: () => {
    set({
      ideas: [],
      isLoadingIdeas: false,
      ideasError: null,
      ideaUiOverrides: {},
    });
  },

  unsubscribeIdeas: () => {
    const subscriptions = get()._ideaSubscriptions;
    console.log(
      `Unsubscribing from ${subscriptions.size} idea subscriptions`,
    );

    subscriptions.forEach((unsubscribe, domainId) => {
      console.log(`Unsubscribing from ideas for domain ${domainId}`);
      unsubscribe();
    });

    set({
      ideas: [],
      _ideaSubscriptions: new Map(),
      ideaUiOverrides: {},
    });
  },

  markIdeaCreatePending: (userId, domainId, ideaId, options) => {
    const existingOverride = get().ideaUiOverrides[ideaId];
    const startedAt =
      options?.startedAt ?? existingOverride?.draftLockStartedAt ?? Date.now();
    const expiresAt =
      options?.expiresAt ??
      existingOverride?.draftLockExpiresAt ??
      startedAt + IDEA_DRAFT_LOCK_DURATION_MS;
    const lock: PersistedIdeaDraftLock = {
      userId,
      domainId,
      ideaId,
      createdDraftPageId:
        options?.pageId ?? existingOverride?.createdDraftPageId ?? null,
      draftLockStartedAt: startedAt,
      draftLockExpiresAt: expiresAt,
      draftLockWpType:
        options?.wpType ?? existingOverride?.draftLockWpType ?? null,
    };

    persistIdeaDraftLock(lock);

    set((state) => ({
      ideaUiOverrides: {
        ...state.ideaUiOverrides,
        [ideaId]: {
          ...state.ideaUiOverrides[ideaId],
          ...getIdeaDraftOverride(lock),
        },
      },
    }));
  },

  resolveIdeaAsImplemented: (
    userId,
    domainId,
    ideaId,
    pageId = null,
    pageType = null,
  ) => {
    clearPersistedIdeaDraftLock(userId, domainId, ideaId);

    set((state) => ({
      ideaUiOverrides: {
        ...state.ideaUiOverrides,
        [ideaId]: {
          ...state.ideaUiOverrides[ideaId],
          isCreatingDraft: false,
          createdDraftPageId: null,
          draftLockStartedAt: undefined,
          draftLockExpiresAt: undefined,
          draftLockWpType: undefined,
          status: "implemented",
          implemented_page_id: pageId,
          implemented_page_type: pageType,
          implemented_at: state.ideaUiOverrides[ideaId]?.implemented_at ?? null,
        },
      },
    }));
  },

  clearIdeaUiOverride: (userId, domainId, ideaId) => {
    clearPersistedIdeaDraftLock(userId, domainId, ideaId);

    set((state) => {
      if (!state.ideaUiOverrides[ideaId]) {
        return {};
      }

      const nextOverrides = { ...state.ideaUiOverrides };
      delete nextOverrides[ideaId];
      return { ideaUiOverrides: nextOverrides };
    });
  },
});
